import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase';

export interface GlobalMetrics {
  // Organization metrics
  totalOrganizations: number;
  totalLocations: number;
  organizationsInPipeline: number;

  // IT task metrics
  itTasksPending: number;
  itTasksInProgress: number;
  itTasksCompleted: number;
  itTasksBlocked: number;

  // Data task metrics
  dataTasksPending: number;
  dataTasksInProgress: number;
  dataTasksCompleted: number;
  dataTasksBlocked: number;

  // Platform task metrics
  platformTasksPending: number;
  platformTasksInProgress: number;
  platformTasksCompleted: number;
}

export function useGlobalMetrics() {
  const [metrics, setMetrics] = useState<GlobalMetrics>({
    totalOrganizations: 0,
    totalLocations: 0,
    organizationsInPipeline: 0,
    itTasksPending: 0,
    itTasksInProgress: 0,
    itTasksCompleted: 0,
    itTasksBlocked: 0,
    dataTasksPending: 0,
    dataTasksInProgress: 0,
    dataTasksCompleted: 0,
    dataTasksBlocked: 0,
    platformTasksPending: 0,
    platformTasksInProgress: 0,
    platformTasksCompleted: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      const supabase = createClient();

      // Fetch organization metrics
      const { count: totalOrgs } = await supabase
        .from('organizations')
        .select('*', { count: 'exact', head: true });

      const { count: totalLocs } = await supabase
        .from('locations')
        .select('*', { count: 'exact', head: true });

      // Count organizations not in ACTIVATED or REJECTED status
      const { count: orgsInPipeline } = await supabase
        .from('organizations')
        .select('*', { count: 'exact', head: true })
        .not('status', 'in', '(ACTIVATED,REJECTED)');

      // Fetch IT task metrics
      const { count: itPending } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['IT_PROVISIONING', 'IT_DECONFIG'])
        .eq('status', 'PENDING');

      const { count: itInProgress } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['IT_PROVISIONING', 'IT_DECONFIG'])
        .eq('status', 'IN_PROGRESS');

      const { count: itCompleted } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['IT_PROVISIONING', 'IT_DECONFIG'])
        .eq('status', 'COMPLETED');

      const { count: itBlocked } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['IT_PROVISIONING', 'IT_DECONFIG'])
        .eq('status', 'BLOCKED');

      // Fetch Data task metrics
      const { count: dataPending } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['DATA_CONFIGURATION', 'DATA_DECONFIG'])
        .eq('status', 'PENDING');

      const { count: dataInProgress } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['DATA_CONFIGURATION', 'DATA_DECONFIG'])
        .eq('status', 'IN_PROGRESS');

      const { count: dataCompleted } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['DATA_CONFIGURATION', 'DATA_DECONFIG'])
        .eq('status', 'COMPLETED');

      const { count: dataBlocked } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['DATA_CONFIGURATION', 'DATA_DECONFIG'])
        .eq('status', 'BLOCKED');

      // Fetch Platform task metrics
      const { count: platformPending } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['MANUAL_REVIEW', 'PAYMENT', 'FINAL_ACTIVATION', 'DEACTIVATION_REVIEW'])
        .eq('status', 'PENDING');

      const { count: platformInProgress } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['MANUAL_REVIEW', 'PAYMENT', 'FINAL_ACTIVATION', 'DEACTIVATION_REVIEW'])
        .eq('status', 'IN_PROGRESS');

      const { count: platformCompleted } = await supabase
        .from('tasks')
        .select('*', { count: 'exact', head: true })
        .in('type', ['MANUAL_REVIEW', 'PAYMENT', 'FINAL_ACTIVATION', 'DEACTIVATION_REVIEW'])
        .eq('status', 'COMPLETED');

      setMetrics({
        totalOrganizations: totalOrgs || 0,
        totalLocations: totalLocs || 0,
        organizationsInPipeline: orgsInPipeline || 0,
        itTasksPending: itPending || 0,
        itTasksInProgress: itInProgress || 0,
        itTasksCompleted: itCompleted || 0,
        itTasksBlocked: itBlocked || 0,
        dataTasksPending: dataPending || 0,
        dataTasksInProgress: dataInProgress || 0,
        dataTasksCompleted: dataCompleted || 0,
        dataTasksBlocked: dataBlocked || 0,
        platformTasksPending: platformPending || 0,
        platformTasksInProgress: platformInProgress || 0,
        platformTasksCompleted: platformCompleted || 0,
      });

      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch metrics'));
      console.error('Error fetching metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  return { metrics, loading, error, refetch: fetchMetrics };
}
